import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { Spinner } from "@components/common/spinner/spinner";
import { PageNotFound } from "@pages/page-not-found";
import { useEffect, useState } from "react";
import { useGroupsOfWorkspace } from "@hooks/use-groups-of-workspace";
import { useMembersOfGroup } from "@hooks/use-members-of-group";
import { CreateProjectContainer } from "@pages/create-project/create-project-container";
import { useAppParams } from "@router/router-helper";
import { useAppNavigation } from "@router/use-app-navigation";
import { RequirePermissionPage } from "@components/common/require-permission-page";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { fetchCompany } from "@store/workspace/workspace-thunks";

/**
 * Fetch the company and group data and call the create project container with the needed data.
 * Only users with the role to create projects see the page, others are sent back to the workspace.
 */
export function CreateProjectForWorkspace(): React.JSX.Element {
  const { companyId, groupId } = useAppParams();
  const { navigateToProfilePage } = useAppNavigation();

  const [selectedGroup, setSelectedGroup] =
    useState<FaroCoreApiTypes.AdmCompanyGroup>();

  const { handleErrorWithPage } = useErrorHandler();

  const { fetchingCompany, company } = useAppSelector(companyStateSelector);
  const dispatch = useAppDispatch();

  // Fetches the company
  useEffect(() => {
    async function fetchCompanyData(): Promise<void> {
      try {
        await dispatch(fetchCompany({ companyId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `CreateProjectForCompany-${Date.now().toString()}`,
          title: "Could not load workspace",
          error,
        });
      }
    }

    void fetchCompanyData();
  }, [dispatch, companyId, handleErrorWithPage]);

  const { groups, isFetchingWorkspaceGroups } = useGroupsOfWorkspace({
    companyId,
    errorHandler: handleErrorWithPage,
  });

  const { groupMembers, isFetchingGroupMembers } = useMembersOfGroup({
    companyId,
    groupId,
    errorHandler: handleErrorWithPage,
  });

  // Find the selected group information from the provided groupId
  useEffect(() => {
    setSelectedGroup(groups.find(({ id }) => id === groupId));
  }, [groupId, groups]);

  let pageContent: React.JSX.Element;

  // TODO: isFetchingGroupMembers can be passed to CreateProjectContainer and show a spinner for project manager field
  if (
    fetchingCompany.selectedCompany === "pending" ||
    isFetchingWorkspaceGroups ||
    isFetchingGroupMembers
  ) {
    pageContent = <Spinner />;
  } else if (!company || !selectedGroup) {
    pageContent = <PageNotFound />;
  } else {
    pageContent = (
      <CreateProjectContainer
        subject={{
          type: "COMPANY",
          company,
          group: selectedGroup,
          groupMembers,
        }}
      />
    );
  }

  return (
    <RequirePermissionPage
      permission="workspace.createProject"
      leaveText="Go to workspace"
      onLeave={() => {
        if (companyId) {
          navigateToProfilePage({
            subjectType: "COMPANY",
            subjectId: companyId,
          });
        }
      }}
    >
      {pageContent}
    </RequirePermissionPage>
  );
}
